import Browser from 'browser-detect';
import Vue from 'vue'
import Vuex from 'vuex'

import BasketApi from '@/api/baskets';
import ProductApi from '@/api/products';
import RestaurantsApi from '@/api/restaurants';
import { MutationTypes } from '@/enums';
import { deDuplicateArray } from '@/utils/global.util';

import plugins from './plugins';

Vue.use(Vuex)

const browser = Browser();
const {
  SET_ACTIVE_CATEGORY,
  SET_ACTIVE_PRODUCT,
  SET_ACTIVE_PRODUCT_MODIFIERS,
  SET_ACTIVE_RESTAURANT,
  SET_ACTIVE_REGION_ID,
  SET_EDIT_IS_OPEN,
  SET_UPSELL_ITEMS,
  SET_HAS_BEEN_UPSOLD,
  SET_MENU,
  SET_HOURS,
  SET_RESTAURANTS,
  STORE_PRODUCT_MODIFIERS,
  SET_PREVIEW_LOCATION,
  CLEAR_PRODUCT_MODIFIER_CACHE,
} = MutationTypes;

const store = new Vuex.Store({
  state: {
    browser,
    menus: {},
    productModifiersCache: {},
    hasBeenUpsold: false,
    editIsOpen: false,
  } as AppState,
  getters: {
    categories: ({ menus, activeRestaurant }) => {
      const { slug } = activeRestaurant || {};
      return menus?.[slug] || [];
    },
    allRestaurants: ({ restaurants }) => {
      return restaurants || [];
    },
    activeRestaurant: ({ activeRestaurant }) => {
      return activeRestaurant || {};
    },
    activeRegionId: ({ activeRegionId }) => {
      return activeRegionId;
    },
    activeCategory: ({ activeCategory }) => {
      return activeCategory;
    },
    activeProduct: ({ activeProduct }) => {
      return activeProduct;
    },
    activeProductModifiers: ({ activeProductModifiers }) => {
      return activeProductModifiers || [];
    },
    basketUpsellItems: ({ upsellItems }) => {
      return upsellItems;
    },
    hasBeenUpsold: ({ hasBeenUpsold }) => {
      return hasBeenUpsold;
    },
    timeWantedTimes: ({ timeWantedTimes }) => {
      return timeWantedTimes;
    },
    timeWantedDays: ({ timeWantedDays }) => {
      return timeWantedDays;
    },
    previewLocation: ({ previewLocation }) => {
      return previewLocation || null;
    },
    editIsOpen: ({ editIsOpen }) => {
      return editIsOpen;
    }
  },
  actions: {
    fetchRestaurantMenu: async ({ commit, state }, { slug, fetchFromState = true }) => {
      try {
        let menu = state.menus?.[slug];
        if (!menu || !fetchFromState) {
          menu = await RestaurantsApi.getMenu(slug);
          Vue.$log.info('fetching menu from api', menu);
          if (menu) {
            commit(SET_MENU, menu);
          }
        } else {
          Vue.$log.info('fetching menu from state', menu);
        }
      } catch (err) {
        Vue.$log.error(err);
      }
    },
    fetchRestaurant: async ({ commit, state }, { restaurantId, fetchFromState = true }) => {
      let restaurant = state.restaurants?.find((r) => r.id === restaurantId);

      if (!restaurant || !fetchFromState) {
        Vue.$log.info('fetching restaurant from api', restaurantId);
        restaurant = await RestaurantsApi.getOne(restaurantId);
        Vue.$log.info('fetchRestaurant', restaurant);
      }

      if (restaurant) {
        commit(SET_ACTIVE_RESTAURANT, restaurant);
        commit(SET_RESTAURANTS, restaurant);
      }

      return restaurant;
    },
    fetchRestaurantBySlug: async ({ commit, state }, { slug, fetchFromState = true }) => {
      let restaurant = state.restaurants?.find((r) => r.slug === slug);

      if (!restaurant || !fetchFromState) {
        Vue.$log.info('fetching restaurant from api', slug);
        restaurant = await RestaurantsApi.getOneBySlug(slug);
        Vue.$log.info('fetchRestaurant', restaurant);
      }

      if (restaurant) {
        commit(SET_ACTIVE_RESTAURANT, restaurant);
        commit(SET_RESTAURANTS, restaurant);
      }

      return restaurant;
    },
    fetchRestaurants: async ({ commit }) => {
      const restaurants = await RestaurantsApi.getAll();
      Vue.$log.info('fetchRestaurants', restaurants);
      commit(SET_RESTAURANTS, restaurants);
    },
    fetchRestaurantsNearby: async ({ commit }, coords: Coords) => {
      const restaurants = await RestaurantsApi.getAllNearby(coords);
      Vue.$log.info('fetchRestaurantsNearby', restaurants, coords);
      if (restaurants?.length) {
        commit(SET_RESTAURANTS, restaurants);
      }
    },
    fetchRegionIdFromCoords: async ({ commit }, coords: Coords) => {
      Vue.$log.info('fetchRegionIdFromCoords', coords);
      const restaurants = await RestaurantsApi.getAllNearby(coords);
      if (restaurants && restaurants.length > 0) {
        const regionId = restaurants[0]?.regionId;
        if (regionId) {
          commit(SET_ACTIVE_REGION_ID, regionId);
          return regionId;
        }
      }
      return null;
    },
    fetchProductModifiers: async ({ commit, state }, { productId, restaurantId }: { productId: number; restaurantId: number }) => {
      try {
        if (!state.productModifiersCache?.[productId]) {
          const modifiers = await ProductApi.getModifiers(productId, restaurantId);
          Vue.$log.info('fetching productModifiers from api', modifiers);
          commit(SET_ACTIVE_PRODUCT_MODIFIERS, modifiers);
          commit(STORE_PRODUCT_MODIFIERS, { productId, modifiers });
        } else {
          const modifiers = state.productModifiersCache?.[productId];
          Vue.$log.info('fetching productModifiers from state', modifiers);
          commit(SET_ACTIVE_PRODUCT_MODIFIERS, modifiers);
        }
      } catch (err) {
        Vue.$log.error(err);
      }
    },
    fetchUpsellItems: async ({ commit }, { basketId }: { basketId: string }) => {
      try {
        const basket = await BasketApi.getUpsellItems(basketId);
        Vue.$log.info('fetching upsell items from api', basket);
        commit(SET_UPSELL_ITEMS, basket);
      } catch (err) {
        Vue.$log.error(err);
      }
    },
  },
  mutations: {
    [SET_MENU](state, menu) {
      Vue.set(state.menus, state.activeRestaurant?.slug, menu);
    },
    [SET_HOURS](state, hours) {
      Vue.set(state.menus, state.activeRestaurant?.slug, hours);
    },
    [SET_RESTAURANTS](state, restaurants) {
      const isSingleRestaurant = !Array.isArray(restaurants);
      let useRestaurants = !isSingleRestaurant ? restaurants : [restaurants];
      if (isSingleRestaurant) {
        useRestaurants = deDuplicateArray((state.restaurants || []).concat(useRestaurants));
      }
      Vue.set(state, 'restaurants', useRestaurants);
    },
    [SET_ACTIVE_CATEGORY](state, category) {
      Vue.set(state, 'activeCategory', category);
    },
    [SET_ACTIVE_PRODUCT](state, product) {
      Vue.set(state, 'activeProduct', product);
    },
    [SET_ACTIVE_PRODUCT_MODIFIERS](state, modifiers) {
      Vue.set(state, 'activeProductModifiers', modifiers);
    },
    [STORE_PRODUCT_MODIFIERS](state, { productId, modifiers }) {
      Vue.set(state.productModifiersCache, productId, modifiers);
    },
    [CLEAR_PRODUCT_MODIFIER_CACHE](state) {
      Vue.set(state, 'productModifiersCache', {});
    },
    [SET_ACTIVE_RESTAURANT](state, restaurant) {
      Vue.set(state, 'activeRestaurant', restaurant);
      Vue.set(state, 'activeRegionId', restaurant?.regionId);
    },
    [SET_ACTIVE_REGION_ID](state, regionId) {
      Vue.set(state, 'activeRegionId', regionId);
    },
    [SET_HAS_BEEN_UPSOLD](state, hasBeenUpsold) {
      Vue.set(state, 'hasBeenUpsold', hasBeenUpsold);
    },
    [SET_UPSELL_ITEMS](state, upsellItems) {
      Vue.set(state, 'upsellItems', upsellItems);
    },
    [SET_PREVIEW_LOCATION](state, previewLocation) {
      Vue.set(state, 'previewLocation', previewLocation);
    },
    [SET_EDIT_IS_OPEN](state, editIsOpen) {
      Vue.set(state, 'editIsOpen', editIsOpen);
    },
  },
  modules: {
  },
  plugins: plugins(),
})

export default store;
